import { ar, enUS } from "date-fns/locale";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { addDays, addMonths, format } from "date-fns";
import { Modal, Box, IconButton, Divider, Typography, Button, Checkbox, FormControlLabel } from "@mui/material";

import APP_ROUTES from "../../../../../routers/routeConstants";
import { StripePayModal, useStripePayModal } from "../../../../../shared/StripePayModal";

import { PLANS_QUERY_KEYS } from "../../../api/queryKeys";
import { CombinedSubscriptionPlan } from "../../../types/plans";
import { UserSubscription } from "../../../api/services/userSettings";
import { CloseButtonIcon } from "../../../assets/icons/credit-modal-icons";
import { useCreateSubscription } from "../../../api/mutations/useCreateSubscription";
import { useUpgradeSubscription } from "../../../api/mutations/useUpgradeSubscription";

import SubscriptionTermsModal from "./SubscriptionTermsModal";

const PaymentSummaryModal = ({
  open,
  closeModal,
  durationInMonths,
  planId,
  planTitle,
  frequency,
  price,
  isSubscribed,

  userSubscription,
  isUpgradable,
}: {
  open: boolean;
  closeModal: () => void;
  durationInMonths: number;
  planId: number;
  planTitle: string;
  frequency: string;
  price: string;
  isSubscribed?: boolean;
  userSubscription?: UserSubscription;
  isUpgradable: boolean;
}) => {
  const { t, i18n } = useTranslation("plans", { keyPrefix: "component.paymentSummaryModal" });
  const [openSubscriptionTermsModal, setOpenSubscriptionTermsModal] = useState(false);
  const handleOpenSubscriptionTermsModal = () => setOpenSubscriptionTermsModal(true);
  const handleCloseSubscriptionTermsModal = () => setOpenSubscriptionTermsModal(false);
  const navigate = useNavigate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [locale, setLocale] = useState(enUS);
  const [createSubscriptionData, setCreateSubscriptionData] = useState<any>(null);
  const [upgradeSubscriptionData, setUpgradeSubscriptionData] = useState<any>(null);

  useEffect(() => {
    setLocale(i18n.language === "ar" ? ar : enUS);
  }, [i18n.language]);

  const currentPlanEndDate = userSubscription?.subscription?.endDate
    ? new Date(userSubscription.subscription.endDate)
    : null;

  const newPlanStartDate = currentPlanEndDate ? addDays(currentPlanEndDate, 1) : new Date(); // Start from today if no current subscription
  const newPlanEndDate = addMonths(newPlanStartDate, durationInMonths);

  const { mutate: createSubscription, isPending: isCreating } = useCreateSubscription((data) => {
    setCreateSubscriptionData(data);
  });

  const { mutate: upgradeSubscription, isPending: isUpgrading } = useUpgradeSubscription((data) => {
    setUpgradeSubscriptionData(data);
  });

  useEffect(() => {
    if (open) {
      if (isUpgradable) {
        upgradeSubscription(planId);
      } else {
        createSubscription(planId);
      }
    } else {
      // Reset states when modal closes
      setCreateSubscriptionData(null);
      setUpgradeSubscriptionData(null);
    }
  }, [open, planId, createSubscription, upgradeSubscription]);

  useEffect(() => {
    if (isSubscribed) {
      setAgreedToTerms(true);
    }
  }, []);
  const handleUpgradeToPlan = () => {
    if (isUpgradable) {
      handleOpenStripePayModal(upgradeSubscriptionData?.clientSecret, upgradeSubscriptionData?.publishableKey);
    } else {
      handleOpenStripePayModal(createSubscriptionData?.clientSecret, createSubscriptionData?.publishableKey);
    }
  };

  const { handleOpenStripePayModal, openStripePayModal, handleCloseStripePayModal, clientSecret, publishableKey } =
    useStripePayModal();

  const onPaymentSuccess = () => {
    closeModal();
    setTimeout(() => {
      navigate(APP_ROUTES.PLANS_OVERVIEW);
    }, 1000);
  };

  const paidPrice = upgradeSubscriptionData?.amountCents
    ? Number(upgradeSubscriptionData.amountCents / 100).toFixed(2)
    : Number(price).toFixed(2);

  return (
    <>
      <Modal open={open} onClose={closeModal}>
        <Box sx={styles.container}>
          <Box sx={styles.header}>
            <Typography variant="h5" sx={styles.title}>
              {t("paymentSummary")}
            </Typography>
            <IconButton size="small" onClick={closeModal}>
              <CloseButtonIcon />
            </IconButton>
          </Box>
          <Divider flexItem orientation="horizontal" />
          <Box sx={styles.content}>
            <Box sx={styles.contentItem}>
              <Typography sx={styles.planTitle}>{planTitle}</Typography>
              <Typography sx={styles.planFrequency}>
                {t("billed")} {t(frequency)}
              </Typography>
            </Box>
            <Box sx={styles.contentItem}>
              <Typography sx={styles.youShouldPay}>{t("youShouldPay")}</Typography>
              <Typography sx={styles.price}>{t("price", { price: paidPrice })}</Typography>
              <Typography sx={styles.forSubscriptionPlan}>{t("forSubscriptionPlan")}</Typography>
              <Divider flexItem orientation="horizontal" />
              <Box sx={styles.planDates}>
                {currentPlanEndDate && (
                  <Typography sx={styles.currentPlanEnds}>
                    {t("currentPlanEnds", {
                      date: currentPlanEndDate ? format(currentPlanEndDate, "MMM dd, yyyy", { locale }) : "",
                    })}
                  </Typography>
                )}
                <Box sx={styles.newPlanDates}>
                  <Typography sx={styles.newPlanStarts}>
                    {t("newPlanStarts", {
                      date: format(newPlanStartDate, "MMM dd, yyyy", { locale }),
                    })}
                  </Typography>
                  <Typography sx={styles.newPlanEnds}>
                    {t("newPlanEnds", {
                      date: format(newPlanEndDate, "MMM dd, yyyy", { locale }),
                    })}
                  </Typography>
                </Box>
              </Box>
              <Divider flexItem orientation="horizontal" />
              {!isSubscribed && (
                <>
                  <Typography sx={styles.yourPlanWillBeExtended}>{t("yourPlanWillBeExtended")}</Typography>
                  <Divider flexItem orientation="horizontal" />
                </>
              )}
            </Box>
            <Typography sx={styles.subscriptionTerms}>{t("subscriptionTerms")}</Typography>
            <FormControlLabel
              value="bottom"
              control={
                <Checkbox
                  size="small"
                  sx={styles.subscriptionRulesCheckbox}
                  checked={agreedToTerms}
                  onChange={() => setAgreedToTerms(!agreedToTerms)}
                />
              }
              sx={styles.subscriptionRulesLabel}
              label={
                <Typography sx={styles.subscriptionRules}>
                  {t("iUnderstandAndAgreeTo")}
                  <Button
                    variant="text-sm"
                    sx={styles.subscriptionRulesLink}
                    onClick={handleOpenSubscriptionTermsModal}
                  >
                    {t("subscriptionRules")}
                  </Button>
                </Typography>
              }
              labelPlacement="bottom"
            />
          </Box>
          <Divider flexItem orientation="horizontal" />
          <Box sx={styles.buttonContainer}>
            <Button variant="outlined-md" onClick={closeModal} sx={styles.cancelButton}>
              {t("cancel")}
            </Button>
            <Button
              variant="primary-md"
              onClick={handleUpgradeToPlan}
              sx={styles.purchaseNowButton}
              disabled={isCreating || !agreedToTerms}
            >
              {t("purchaseNow")}
            </Button>
          </Box>
        </Box>
      </Modal>
      <StripePayModal
        open={openStripePayModal}
        closeModal={handleCloseStripePayModal}
        clientSecret={clientSecret}
        publishableKey={publishableKey}
        queryKey={[
          ...PLANS_QUERY_KEYS.SUBSCRIPTION_TYPES,
          ...PLANS_QUERY_KEYS.CURRENT_PLAN,
          ...PLANS_QUERY_KEYS.USER_SUBSCRIPTION,
        ]}
        onSuccess={onPaymentSuccess}
      />
      <SubscriptionTermsModal open={openSubscriptionTermsModal} closeModal={handleCloseSubscriptionTermsModal} />
    </>
  );
};

export default PaymentSummaryModal;

const styles = {
  container: {
    height: "fit-content",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "var(--radius-xl, 12px)",
    background: "var(--Colors-Background-bg-primary, #FFF)",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
    width: "640px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 24px 0 24px",
  },
  title: {
    color: "var(--colors-text-text-primary-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "0 24px",
  },
  contentItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  planTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  planFrequency: {
    color: "var(--Brand-600, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  youShouldPay: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  price: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
  },
  forSubscriptionPlan: {
    mb: "8px",
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  subscriptionTerms: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  subscriptionRules: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  subscriptionRulesLink: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111) !important",
    background: "transparent !important",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
    textDecoration: "underline",
    padding: "0",
  },
  subscriptionRulesLabel: {
    padding: "0",
    margin: "0",
    flexDirection: "row",
    gap: "4px",
  },
  subscriptionRulesCheckbox: {
    padding: "0",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "0 24px 24px",
  },
  cancelButton: {
    color: "var(--Gray-700, #344054) !important",
    fontFamily: "Montserrat",
    border: "1px solid var(--Gray-300, #D0D5DD) !important",
    background: "var(--Base-White, #FFF) !important",
  },
  purchaseNowButton: {
    fontFamily: "Montserrat",
  },
  currentPlanEnds: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  newPlanStarts: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  newPlanEnds: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  planDates: {
    my: "8px",
    display: "flex",
    justifyContent: "space-between",
  },
  newPlanDates: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  yourPlanWillBeExtended: {
    my: "8px",
    color: "var(--Dark---1000, #111) !important",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};
